import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';

const NETWORK_ORDER = ['hookah', 'tabakov_mgn', 'tabakov_chel'];

function activeOnly(items = []) {
  return items.filter(item => item.is_active !== false);
}

function sortNetworks(networks) {
  return [...networks].sort((a, b) => {
    const ai = NETWORK_ORDER.indexOf(a.slug);
    const bi = NETWORK_ORDER.indexOf(b.slug);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id;
  });
}

function primaryZone(network) {
  return activeOnly(network.zones)[0] || null;
}

function allPoints(network) {
  const zoned = activeOnly(network.zones).flatMap(zone =>
    activeOnly(zone.points).map(point => ({ ...point, zone_id: point.zone_id || zone.id }))
  );
  return [...zoned, ...activeOnly(network.points)].sort((a, b) =>
    (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
  );
}

export default function NetworkSettings() {
  const [networks, setNetworks] = useState([]);
  const [networkDrafts, setNetworkDrafts] = useState({});
  const [pointDrafts, setPointDrafts] = useState({});
  const [newPoints, setNewPoints] = useState({});
  const [newNetwork, setNewNetwork] = useState({ name: '', slug: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const orderedNetworks = useMemo(() => sortNetworks(activeOnly(networks)), [networks]);

  const hasChanges = useMemo(() => {
    const changedNetworks = orderedNetworks.some(network => {
      const draft = networkDrafts[network.id] || {};
      return (draft.name ?? network.name) !== network.name || (draft.slug ?? network.slug) !== network.slug;
    });
    const changedPoints = orderedNetworks.some(network =>
      allPoints(network).some(point => (pointDrafts[point.id] ?? point.name) !== point.name)
    );
    const createdPoints = Object.values(newPoints).some(items => items.some(item => item.name.trim()));
    return changedNetworks || changedPoints || createdPoints || newNetwork.name.trim();
  }, [networkDrafts, newNetwork.name, newPoints, orderedNetworks, pointDrafts]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/admin/settings/networks');
      setNetworks(res.data.networks || []);
      setNetworkDrafts({});
      setPointDrafts({});
      setNewPoints({});
      setNewNetwork({ name: '', slug: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить настройки сетей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateNetworkDraft = (network, patch) => {
    setNetworkDrafts(prev => ({
      ...prev,
      [network.id]: {
        name: prev[network.id]?.name ?? network.name,
        slug: prev[network.id]?.slug ?? network.slug,
        ...patch,
      },
    }));
  };

  const updatePointDraft = (point, name) => {
    setPointDrafts(prev => ({ ...prev, [point.id]: name }));
  };

  const addPointRow = (network) => {
    const tempId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setNewPoints(prev => ({
      ...prev,
      [network.id]: [...(prev[network.id] || []), { tempId, name: '' }],
    }));
  };

  const updateNewPoint = (networkId, tempId, name) => {
    setNewPoints(prev => ({
      ...prev,
      [networkId]: (prev[networkId] || []).map(item => item.tempId === tempId ? { ...item, name } : item),
    }));
  };

  const removeNewPoint = (networkId, tempId) => {
    setNewPoints(prev => ({
      ...prev,
      [networkId]: (prev[networkId] || []).filter(item => item.tempId !== tempId),
    }));
  };

  const saveAll = async () => {
    setSaving(true);
    setError('');
    setSavedAt(null);
    try {
      if (newNetwork.name.trim()) {
        await api.post('/api/admin/settings/networks', {
          name: newNetwork.name.trim(),
          slug: newNetwork.slug.trim() || undefined,
        });
      }

      for (const network of orderedNetworks) {
        const draft = networkDrafts[network.id] || {};
        const name = (draft.name ?? network.name).trim();
        const slug = (draft.slug ?? network.slug).trim();
        if (name && (name !== network.name || slug !== network.slug)) {
          await api.put(`/api/admin/settings/networks/${network.id}`, { name, slug });
        }

        for (const point of allPoints(network)) {
          const nameDraft = pointDrafts[point.id];
          const nameValue = (nameDraft ?? point.name).trim();
          if (nameValue && nameValue !== point.name) {
            await api.put(`/api/admin/settings/points/${point.id}`, {
              name: nameValue,
              zone_id: point.zone_id || null,
              sort_order: point.sort_order,
            });
          }
        }

        for (const item of newPoints[network.id] || []) {
          const nameValue = item.name.trim();
          if (nameValue) {
            await api.post(`/api/admin/settings/networks/${network.id}/points`, {
              name: nameValue,
              zone_id: primaryZone(network)?.id || null,
            });
          }
        }
      }

      await load();
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loader">Загрузка...</div>;

  return (
    <div className="page networks-page">
      <div className="page-header networks-header">
        <div>
          <h1>Сети и точки</h1>
          <p className="page-subtitle">Простой справочник магазинов. Эти точки сразу используются при создании заявок.</p>
        </div>
        <div className="networks-header-actions">
          {savedAt && <span className="save-state">Сохранено {savedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>}
          {hasChanges && !savedAt && <span className="save-state pending">Есть несохраненные изменения</span>}
          <button className="btn-primary" onClick={saveAll} disabled={saving || !hasChanges}>
            {saving ? 'Сохраняю...' : 'Сохранить изменения'}
          </button>
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div className="light-add-network">
        <input
          type="text"
          value={newNetwork.name}
          onChange={e => setNewNetwork(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Добавить новую сеть"
        />
        <input
          type="text"
          value={newNetwork.slug}
          onChange={e => setNewNetwork(prev => ({ ...prev, slug: e.target.value }))}
          placeholder="Код, необязательно"
        />
      </div>

      <div className="light-networks-board">
        {orderedNetworks.map(network => {
          const points = allPoints(network);
          const networkDraft = networkDrafts[network.id] || {};
          const created = newPoints[network.id] || [];

          return (
            <section key={network.id} className="light-network-column">
              <div className="light-network-top">
                <div className="network-label">Сеть</div>
                <input
                  type="text"
                  className="network-name-field"
                  value={networkDraft.name ?? network.name}
                  onChange={e => updateNetworkDraft(network, { name: e.target.value })}
                />
                <input
                  type="text"
                  className="network-code-field"
                  value={networkDraft.slug ?? network.slug}
                  onChange={e => updateNetworkDraft(network, { slug: e.target.value })}
                />
                <span>{points.length} точек</span>
              </div>

              <div className="light-point-list">
                {points.map(point => (
                  <input
                    key={point.id}
                    type="text"
                    className="point-name-field"
                    value={pointDrafts[point.id] ?? point.name}
                    onChange={e => updatePointDraft(point, e.target.value)}
                  />
                ))}

                {created.map(item => (
                  <div key={item.tempId} className="new-point-row">
                    <input
                      type="text"
                      className="point-name-field"
                      value={item.name}
                      onChange={e => updateNewPoint(network.id, item.tempId, e.target.value)}
                      placeholder="Новая точка"
                    />
                    <button type="button" onClick={() => removeNewPoint(network.id, item.tempId)}>×</button>
                  </div>
                ))}
              </div>

              <button className="add-light-point" onClick={() => addPointRow(network)}>
                + Добавить точку
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
}
